import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { LaborStationOperatorPresence } from "@/components/labor/LaborStationOperatorPresence";
import { ApiError } from "@/lib/api";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";
import { usePageBreadcrumb } from "@/lib/navigationTrailContext";

import { InboundStationLaborTracker } from "./InboundStationLaborTracker";
import { InboundToStockPutAwayModal } from "./InboundToStockPutAwayModal";
import { receivingLaborWirePage } from "./inboundLaborPage";
import {
  confirmToStockPutAway,
  formatNumber,
  lookupToStock,
  type ToStockLine,
  type ToStockLookupResult,
} from "./inboundApi";

const TO_STOCK_HOVER_HINT =
  "Scan a clear inventory tag (not a HOLD tag) and confirm the carton is on the shelf. "
  + "Pick an existing sub location for this SKU or enter a new one. "
  + "Arrived package tags belong on Arrivals, not here.";

type PutAwayTarget = {
  line: ToStockLine;
  inventoryBoxHex: string | null;
  sublocationHints: string[];
  hintsAreLastShelf: boolean;
};

export function InboundToStockPage(): JSX.Element {
  usePageBreadcrumb("To stock");
  const queryClient = useQueryClient();
  const scanRef = useRef<HTMLInputElement | null>(null);
  const [searchParams] = useSearchParams();
  const openedScan = useRef("");
  const [scan, setScan] = useState(() => searchParams.get("scan")?.trim() ?? "");
  const [lookup, setLookup] = useState<ToStockLookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [putAwayTarget, setPutAwayTarget] = useState<PutAwayTarget | null>(null);

  const queueQuery = useQuery({
    queryKey: ["shipping", "inbound", "to-stock"],
    queryFn: () => lookupToStock(),
  });

  const lookupMutation = useMutation({
    mutationFn: (value: string) => lookupToStock(value),
    onSuccess: (result) => {
      setError(null);
      setLookup(result);
      if (result.kind === "to_stock" && result.rows.length === 1) {
        const line = result.rows[0]!;
        setPutAwayTarget({
          line,
          inventoryBoxHex: result.inventory_box_hex ?? null,
          sublocationHints: result.sku_sublocations ?? [],
          hintsAreLastShelf: result.sku_sublocations_are_last === true,
        });
      }
    },
    onError: (mutationError) =>
      setError(mutationError instanceof ApiError ? mutationError.message : String(mutationError)),
  });

  const putAwayMutation = useMutation({
    mutationFn: (input: PutAwayTarget & { sublocation: string }) =>
      confirmToStockPutAway({
        inbound_receipt_item_id: input.line.id,
        sublocation: input.sublocation,
        inventory_box_hex: input.inventoryBoxHex,
      }),
    onSuccess: () => {
      setError(null);
      setPutAwayTarget(null);
      setLookup(null);
      setScan("");
      void queryClient.invalidateQueries({ queryKey: ["shipping", "inbound", "to-stock"] });
    },
    onError: (mutationError) =>
      setError(mutationError instanceof ApiError ? mutationError.message : String(mutationError)),
  });

  const submitScan = (value: string) => {
    const trimmed = value.trim();
    if (trimmed === "") return;
    lookupMutation.mutate(trimmed);
  };

  const scanFromUrl = searchParams.get("scan")?.trim() ?? "";
  useEffect(() => {
    if (scanFromUrl === "" || openedScan.current === scanFromUrl) return;
    openedScan.current = scanFromUrl;
    setScan(scanFromUrl);
    lookupMutation.mutate(scanFromUrl);
  }, [scanFromUrl, lookupMutation]);

  useFloorPageScanCapture({
    enabled: putAwayTarget == null,
    inputRef: scanRef,
    onScan: (value) => {
      setScan(value);
      submitScan(value);
    },
  });

  const queueRows = queueQuery.data?.kind === "to_stock" ? queueQuery.data.rows : [];

  const openPutAway = (line: ToStockLine, context?: ToStockLookupResult | null) => {
    const hints =
      context?.kind === "to_stock" ? (context.sku_sublocations ?? []) : [];
    const hintsAreLastShelf =
      context?.kind === "to_stock" && context.sku_sublocations_are_last === true;
    setPutAwayTarget({
      line,
      inventoryBoxHex:
        context?.kind === "to_stock" ? (context.inventory_box_hex ?? null) : null,
      sublocationHints: hints,
      hintsAreLastShelf,
    });
    if (context != null) return;
    void lookupToStock(line.sku).then((lookedUp) => {
      if (lookedUp.kind !== "to_stock") return;
      setPutAwayTarget((current) => {
        if (current == null || current.line.id !== line.id) return current;
        return {
          ...current,
          sublocationHints: lookedUp.sku_sublocations ?? [],
          hintsAreLastShelf: lookedUp.sku_sublocations_are_last === true,
        };
      });
    }).catch(() => undefined);
  };

  return (
    <div className="space-y-4">
      <InboundStationLaborTracker wirePage={receivingLaborWirePage("to-stock")} workLabel="To stock" />
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={TO_STOCK_HOVER_HINT}>To stock</PageTitleWithHelp>
        <LaborStationOperatorPresence stationPage="receiving" receivingFloor="to-stock" />
      </header>

      {error ? (
        <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </p>
      ) : null}

      <Card>
        <CardBody className="space-y-3">
          <div>
            <Label htmlFor="to-stock-scan">Inventory tag or SKU</Label>
            <Input
              id="to-stock-scan"
              ref={scanRef}
              value={scan}
              onChange={(event) => setScan(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  submitScan(scan);
                }
              }}
              placeholder="Scan the Data Matrix URL or type the SKU"
              autoComplete="off"
              {...FLOOR_PAGE_SCAN_TARGET_PROPS}
            />
          </div>
          <Button
            type="button"
            onClick={() => submitScan(scan)}
            disabled={scan.trim() === "" || lookupMutation.isPending}
          >
            {lookupMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Look up
          </Button>
          <LookupResult
            lookup={lookup}
            onPutAway={(line) => openPutAway(line, lookup)}
          />
        </CardBody>
      </Card>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-900">Ready to put away</h2>
        {queueQuery.isLoading ? (
          <p className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading.
          </p>
        ) : queueRows.length === 0 ? (
          <Card>
            <CardBody>
              <p className="text-sm text-slate-500">Nothing waiting on a clear tag.</p>
            </CardBody>
          </Card>
        ) : (
          <ul className="space-y-2">
            {queueRows.map((row) => (
              <ToStockRowCard key={row.id} row={row} onPutAway={() => openPutAway(row, null)} />
            ))}
          </ul>
        )}
      </section>

      <InboundToStockPutAwayModal
        open={putAwayTarget != null}
        sku={putAwayTarget?.line.sku ?? ""}
        sublocationHints={putAwayTarget?.sublocationHints ?? []}
        hintsAreLastShelf={putAwayTarget?.hintsAreLastShelf === true}
        pending={putAwayMutation.isPending}
        onCancel={() => setPutAwayTarget(null)}
        onConfirm={(sublocation) => {
          if (putAwayTarget == null) return;
          putAwayMutation.mutate({ ...putAwayTarget, sublocation });
        }}
      />
    </div>
  );
}

function LookupResult({
  lookup,
  onPutAway,
}: {
  lookup: ToStockLookupResult | null;
  onPutAway: (line: ToStockLine) => void;
}): JSX.Element | null {
  if (lookup == null) return null;
  if (lookup.kind === "unknown") {
    return <p className="text-sm text-slate-600">No matching carton or inventory line for that scan.</p>;
  }
  if (lookup.kind === "arrival_package") {
    const pkg = lookup.package;
    return (
      <p className="text-sm text-amber-800">
        That is an Arrivals package tag ({pkg.vendor_name}
        {pkg.po_number != null ? `, ${pkg.po_number}` : ""}, carton {pkg.package_no}). Open Arrivals, not To stock.
      </p>
    );
  }
  if (lookup.rows.length === 0) {
    return (
      <p className="text-sm text-slate-600">
        {lookup.sku ?? "This SKU"} is not ready for To stock. It may still be in Holding, or already put away.
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {lookup.rows.map((row) => (
        <ToStockRowCard key={row.id} row={row} onPutAway={() => onPutAway(row)} />
      ))}
    </ul>
  );
}

function ToStockRowCard({
  row,
  onPutAway,
}: {
  row: ToStockLine;
  onPutAway: () => void;
}): JSX.Element {
  const source = row.source_number ?? `${row.source_type} ${row.source_id}`;
  return (
    <Card>
      <CardBody className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-mono text-sm font-semibold text-slate-900">{row.sku}</p>
          <p className="text-sm text-slate-600">
            {source} · {formatNumber(row.received_quantity)} received
          </p>
        </div>
        <Button size="sm" onClick={onPutAway}>
          Put away
        </Button>
      </CardBody>
    </Card>
  );
}
